import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { jsx } from 'react/jsx-runtime';
import { useRef, useEffect, useCallback, createContext, useContext, useState } from 'react';
import { useSpring, animated, config, useTransition } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import { Subject } from 'rxjs';
import screenfull from 'screenfull';

function useCustomEventsModule() {
  var eventsObserverRef = useRef(new Subject());

  function useListenToCustomEvent(fn) {
    useEffect(function () {
      var subscribe = eventsObserverRef.current.subscribe(fn);
      return function () {
        return subscribe.unsubscribe();
      };
    }, [fn]);
  }

  var emitObservable = function emitObservable(data) {
    eventsObserverRef.current.next(data);
  };

  return {
    useListenToCustomEvent: useListenToCustomEvent,
    emitObservable: emitObservable
  };
}

function useFullscreenModule(_ref) {
  var mainCarouselWrapperRef = _ref.mainCarouselWrapperRef,
      emitObservable = _ref.emitObservable,
      handleResize = _ref.handleResize;
  var isFullscreen = useRef(false);
  useEffect(function () {
    function handleFullscreenChange() {
      if (document.fullscreenElement) {
        setIsFullscreen(true);
        emitObservable({
          eventName: 'onFullscreenChange',
          isFullscreen: true
        });
        handleResize && handleResize();
      }

      if (!document.fullscreenElement) {
        setIsFullscreen(false);
        emitObservable({
          eventName: 'onFullscreenChange',
          isFullscreen: false
        });
        handleResize && handleResize();
      }
    }

    if (screenfull.isEnabled) {
      screenfull.on('change', handleFullscreenChange);
      return function () {
        if (screenfull.isEnabled) {
          screenfull.off('change', handleFullscreenChange);
        }
      };
    }
  });

  function setIsFullscreen(_isFullscreen) {
    isFullscreen.current = _isFullscreen;
  }

  function getIsFullscreen() {
    return isFullscreen.current;
  }

  function enterFullscreen(elementRef) {
    if (screenfull.isEnabled) {
      screenfull.request(elementRef || mainCarouselWrapperRef.current);
    }
  }

  function exitFullscreen() {
    screenfull.isEnabled && screenfull.exit();
  }

  return {
    enterFullscreen: enterFullscreen,
    exitFullscreen: exitFullscreen,
    getIsFullscreen: getIsFullscreen
  };
}

function useMount(callback) {
  var isMounted = useRef(false);
  useEffect(function () {
    if (!isMounted.current) {
      var clean = callback();
      isMounted.current = true;
      return function () {
        clean && clean();
      };
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
}

function getIsBrowser() {
  return typeof window !== 'undefined';
}

function useThumbsModule(_ref2) {
  var items = _ref2.items,
      withThumbs = _ref2.withThumbs,
      _ref2$thumbsSlideAxis = _ref2.thumbsSlideAxis,
      thumbsSlideAxis = _ref2$thumbsSlideAxis === void 0 ? 'x' : _ref2$thumbsSlideAxis,
      springConfig = _ref2.springConfig,
      thumbsWrapperRef = _ref2.thumbsWrapperRef,
      prepareThumbsData = _ref2.prepareThumbsData;
  var internalThumbsWrapperRef = useRef(null);

  var _useSpring = useSpring(function () {
    var _ref3;

    return _ref3 = {}, _defineProperty(_ref3, thumbsSlideAxis, 0), _defineProperty(_ref3, "config", springConfig), _ref3;
  }),
      _useSpring2 = _slicedToArray(_useSpring, 2),
      thumbListStyles = _useSpring2[0],
      setThumbListStyles = _useSpring2[1];

  useMount(function () {
    if (withThumbs) {
      var missingThumbs = items.some(function (item) {
        return !item.renderThumb;
      });

      if (missingThumbs) {
        throw new Error('The renderThumb property is missing in one or more items. You need to add the renderThumb property to every item of the carousel when the prop withThumbs={true} or eventually set withThumbs={false}.');
      }
    }
  });

  function handleThumbsScroll(activeItem) {
    function getOffsetDirection() {
      return thumbsSlideAxis === 'x' ? 'offsetLeft' : 'offsetTop';
    }

    function getOffsetDimension() {
      return thumbsSlideAxis === 'x' ? 'offsetWidth' : 'offsetHeight';
    }

    function getScrollDirecton() {
      return thumbsSlideAxis === 'x' ? 'scrollLeft' : 'scrollTop';
    }

    function getThumbNode() {
      return internalThumbsWrapperRef.current.querySelector("#thumb-".concat(items[activeItem].id));
    }

    function getThumbOffsetPosition(_ref4) {
      var thumbNode = _ref4.thumbNode,
          offsetDirection = _ref4.offsetDirection,
          offsetDimension = _ref4.offsetDimension;
      return thumbNode[offsetDirection] + thumbNode[offsetDimension] / 2;
    }

    function getThumbScrollDimension(_ref5) {
      var thumbWrapper = _ref5.thumbWrapper,
          offsetDimension = _ref5.offsetDimension;
      return thumbWrapper[offsetDimension] / 2;
    }

    function getScrollFromValue(_ref6) {
      var thumbWrapper = _ref6.thumbWrapper,
          scrollDirection = _ref6.scrollDirection;
      return thumbWrapper[scrollDirection];
    }

    function getScrollToValue(_ref7) {
      var thumbWrapper = _ref7.thumbWrapper,
          thumbOffsetPosition = _ref7.thumbOffsetPosition,
          thumbScrollDimension = _ref7.thumbScrollDimension,
          offsetDimension = _ref7.offsetDimension;
      var scrollDimensionProperty = thumbsSlideAxis === 'x' ? 'scrollWidth' : 'scrollHeight';

      if (activeItem === items.length - 1 || thumbOffsetPosition - thumbScrollDimension > thumbWrapper[scrollDimensionProperty] - thumbWrapper[offsetDimension]) {
        return thumbWrapper[scrollDimensionProperty] - thumbWrapper[offsetDimension];
      }

      if (activeItem === 0) {
        return 0;
      }

      return thumbOffsetPosition - thumbScrollDimension;
    }

    if (thumbsWrapperRef && thumbsWrapperRef.current) {
      internalThumbsWrapperRef.current = thumbsWrapperRef.current;
    }

    var thumbNode = getThumbNode();

    if (thumbNode) {
      var thumbWrapper = internalThumbsWrapperRef.current;
      var offsetDirection = getOffsetDirection();
      var offsetDimension = getOffsetDimension();
      var scrollDirection = getScrollDirecton();
      var thumbOffsetPosition = getThumbOffsetPosition({
        thumbNode: thumbNode,
        offsetDimension: offsetDimension,
        offsetDirection: offsetDirection
      });
      var thumbScrollDimension = getThumbScrollDimension({
        thumbWrapper: thumbWrapper,
        offsetDimension: offsetDimension
      });
      setThumbListStyles.start({
        from: _defineProperty({}, thumbsSlideAxis, getScrollFromValue({
          thumbWrapper: thumbWrapper,
          scrollDirection: scrollDirection
        })),
        to: _defineProperty({}, thumbsSlideAxis, getScrollToValue({
          thumbWrapper: thumbWrapper,
          thumbOffsetPosition: thumbOffsetPosition,
          thumbScrollDimension: thumbScrollDimension,
          offsetDimension: offsetDimension
        })),
        onChange: function onChange(val) {
          if (thumbsSlideAxis === 'x') {
            // @ts-ignore
            internalThumbsWrapperRef.current.scrollLeft = val.x;
          } else {
            // @ts-ignore
            internalThumbsWrapperRef.current.scrollTop = val.y;
          }
        }
      });
    }
  }

  function handlePrepareThumbsDate() {
    function getPreparedItems(_items) {
      return _items.map(function (i) {
        return {
          id: i.id,
          renderThumb: i.renderThumb
        };
      });
    }

    if (prepareThumbsData) {
      return prepareThumbsData(getPreparedItems(items));
    }

    return getPreparedItems(items);
  }

  function getScrollDirectionSpringValue() {
    if (thumbsSlideAxis === 'x') {
      return {
        // @ts-ignore
        scrollLeft: thumbListStyles.x
      };
    }

    return {
      // @ts-ignore
      scrollTop: thumbListStyles.y
    };
  }

  var thumbsFragment = withThumbs ? jsx(animated.div, Object.assign({
    ref: internalThumbsWrapperRef
  }, getScrollDirectionSpringValue(), {
    style: _objectSpread({
      display: 'flex',
      flex: 1,
      position: 'relative',
      flexDirection: thumbsSlideAxis === 'x' ? 'row' : 'column'
    }, thumbsSlideAxis === 'x' ? {
      overflowX: 'auto'
    } : {
      overflowY: 'auto',
      maxHeight: '100%'
    })
  }, {
    children: handlePrepareThumbsDate().map(function (_ref8) {
      var id = _ref8.id,
          renderThumb = _ref8.renderThumb;
      var thumbId = "thumb-".concat(id);
      return jsx("div", Object.assign({
        id: thumbId
      }, {
        children: renderThumb
      }), thumbId);
    })
  }), void 0) : null;
  return {
    thumbsFragment: thumbsFragment,
    handleThumbsScroll: handleThumbsScroll
  };
}

var UseSpringCarouselContext = createContext(undefined);

function useSpringCarouselContext() {
  var context = useContext(UseSpringCarouselContext);

  if (!context) {
    throw new Error("useSpringCarouselContext isn't being used within the useSringCarousel context; \n    use the context only inside a component that is rendered within the Carousel.");
  }

  return context;
}

function useSpringCarousel(_ref9) {
  var _objectSpread2;

  var items = _ref9.items,
      _ref9$withLoop = _ref9.withLoop,
      withLoop = _ref9$withLoop === void 0 ? false : _ref9$withLoop,
      _ref9$draggingSlideTr = _ref9.draggingSlideTreshold,
      draggingSlideTreshold = _ref9$draggingSlideTr === void 0 ? 100 : _ref9$draggingSlideTr,
      _ref9$springConfig = _ref9.springConfig,
      springConfig = _ref9$springConfig === void 0 ? config.default : _ref9$springConfig,
      _ref9$shouldResizeOnW = _ref9.shouldResizeOnWindowResize,
      shouldResizeOnWindowResize = _ref9$shouldResizeOnW === void 0 ? true : _ref9$shouldResizeOnW,
      _ref9$withThumbs = _ref9.withThumbs,
      withThumbs = _ref9$withThumbs === void 0 ? false : _ref9$withThumbs,
      _ref9$enableThumbsWra = _ref9.enableThumbsWrapperScroll,
      enableThumbsWrapperScroll = _ref9$enableThumbsWra === void 0 ? true : _ref9$enableThumbsWra,
      _ref9$carouselSlideAx = _ref9.carouselSlideAxis,
      carouselSlideAxis = _ref9$carouselSlideAx === void 0 ? 'x' : _ref9$carouselSlideAx,
      _ref9$thumbsSlideAxis = _ref9.thumbsSlideAxis,
      thumbsSlideAxis = _ref9$thumbsSlideAxis === void 0 ? 'x' : _ref9$thumbsSlideAxis,
      thumbsWrapperRef = _ref9.thumbsWrapperRef,
      prepareThumbsData = _ref9.prepareThumbsData,
      _ref9$itemsPerSlide = _ref9.itemsPerSlide,
      itemsPerSlide = _ref9$itemsPerSlide === void 0 ? 1 : _ref9$itemsPerSlide,
      _ref9$initialActiveIt = _ref9.initialActiveItem,
      initialActiveItem = _ref9$initialActiveIt === void 0 ? 0 : _ref9$initialActiveIt,
      _ref9$initialStarting = _ref9.initialStartingPosition,
      initialStartingPosition = _ref9$initialStarting === void 0 ? 'start' : _ref9$initialStarting,
      _ref9$disableGestures = _ref9.disableGestures,
      disableGestures = _ref9$disableGestures === void 0 ? false : _ref9$disableGestures,
      _ref9$gutter = _ref9.gutter,
      gutter = _ref9$gutter === void 0 ? 0 : _ref9$gutter,
      _ref9$adjacentItemsPx = _ref9.adjacentItemsPx,
      adjacentItemsPx = _ref9$adjacentItemsPx === void 0 ? 0 : _ref9$adjacentItemsPx;

  function getItems() {
    if (withLoop) {
      return [].concat(_toConsumableArray(items), _toConsumableArray(items), _toConsumableArray(items));
    }

    return items;
  }

  var slideActionType = useRef('next');
  var internalItems = getItems();
  var activeItem = useRef(initialActiveItem);
  var mainCarouselWrapperRef = useRef(null);
  var carouselTrackWrapperRef = useRef(null);
  var isDragging = useRef(false);
  var isAnimating = useRef(false);
  var windowIsHidden = useRef(false);

  function getCarouselItem() {
    if (carouselTrackWrapperRef.current) {
      return carouselTrackWrapperRef.current.querySelector('.use-spring-carousel-item');
    }

    return null;
  }

  var _useSpring3 = useSpring(function () {
    return {
      y: 0,
      x: 0,
      config: springConfig
    };
  }),
      _useSpring4 = _slicedToArray(_useSpring3, 2),
      carouselStyles = _useSpring4[0],
      setCarouselStyles = _useSpring4[1];

  var getSlideValue = useCallback(function () {
    if (!carouselTrackWrapperRef.current) {
      return 0;
    }

    var carouselItem = getCarouselItem();

    if (!carouselItem) {
      throw Error('No carousel items available!');
    }

    if (carouselSlideAxis === 'x') {
      return carouselItem.getBoundingClientRect().width + gutter;
    }

    return carouselItem.getBoundingClientRect().height + gutter;
  }, [carouselSlideAxis, gutter]);
  var adjustCarouselWrapperPosition = useCallback(function (ref) {
    var positionProperty = carouselSlideAxis === 'x' ? 'left' : 'top';

    function getDefaultPositionValue() {
      return getSlideValue() * items.length;
    }

    function setPosition(v) {
      ref.style.top = '0px';
      ref.style.left = '0px';
      ref.style[positionProperty] = "-".concat(v - adjacentItemsPx, "px");
    }

    function setStartPosition() {
      setPosition(getDefaultPositionValue());
    }

    function setCenterPosition() {
      setPosition(getDefaultPositionValue() - getSlideValue() * Math.round((itemsPerSlide - 1) / 2));
    }

    function setEndPosition() {
      setPosition(getDefaultPositionValue() - getSlideValue() * Math.round(itemsPerSlide - 1));
    }

    if (itemsPerSlide > 1) {
      switch (initialStartingPosition) {
        default:
        case 'start':
          {
            setStartPosition();
            break;
          }

        case 'center':
          {
            setCenterPosition();
            break;
          }

        case 'end':
          {
            setEndPosition();
            break;
          }
      }
    } else {
      setStartPosition();
    }
  }, [carouselSlideAxis, getSlideValue, initialStartingPosition, items.length, itemsPerSlide, adjacentItemsPx]);
  var handleResize = useCallback(function () {
    setCarouselStyles.start({
      immediate: true,
      x: 0,
      y: 0
    });
    setCarouselStyles.start(_defineProperty({
      immediate: true
    }, carouselSlideAxis, -(getSlideValue() * _getCurrentActiveItem())));

    if (withLoop) {
      adjustCarouselWrapperPosition(carouselTrackWrapperRef.current);
    }
  }, [adjustCarouselWrapperPosition, carouselSlideAxis, getSlideValue, setCarouselStyles, withLoop]); // Custom modules

  var _useCustomEventsModul = useCustomEventsModule(),
      useListenToCustomEvent = _useCustomEventsModul.useListenToCustomEvent,
      emitObservable = _useCustomEventsModul.emitObservable;

  var _useFullscreenModule = useFullscreenModule({
    mainCarouselWrapperRef: mainCarouselWrapperRef,
    emitObservable: emitObservable,
    handleResize: handleResize
  }),
      enterFullscreen = _useFullscreenModule.enterFullscreen,
      exitFullscreen = _useFullscreenModule.exitFullscreen,
      getIsFullscreen = _useFullscreenModule.getIsFullscreen;

  var _useThumbsModule = useThumbsModule({
    withThumbs: withThumbs,
    items: items,
    thumbsSlideAxis: thumbsSlideAxis,
    springConfig: springConfig,
    thumbsWrapperRef: thumbsWrapperRef,
    prepareThumbsData: prepareThumbsData
  }),
      _thumbsFragment = _useThumbsModule.thumbsFragment,
      handleThumbsScroll = _useThumbsModule.handleThumbsScroll;

  var bindDrag = useDrag(function (props) {
    var isDragging = props.dragging;
    var movement = props.movement[carouselSlideAxis === 'x' ? 0 : 1];
    var currentSlidedValue = -(getSlideValue() * _getCurrentActiveItem());

    function resetAnimation() {
      setCarouselStyles.start(_defineProperty({}, carouselSlideAxis, currentSlidedValue));
    }

    if (isDragging) {
      setCarouselStyles.start(_defineProperty({}, carouselSlideAxis, currentSlidedValue + movement));
      setIsDragging(true);
      emitObservable(_objectSpread({
        eventName: 'onDrag'
      }, props));
      var prevItemTreshold = movement > draggingSlideTreshold;
      var nextItemTreshold = movement < -draggingSlideTreshold;

      if (nextItemTreshold) {
        if (!withLoop && getIsLastItem()) {
          resetAnimation();
        } else {
          slideToNextItem();
        }

        props.cancel();
      } else if (prevItemTreshold) {
        if (!withLoop && getIsFirstItem()) {
          resetAnimation();
        } else {
          slideToPrevItem();
        }

        props.cancel();
      }
    }

    if (props.last) {
      resetAnimation();
    }
  }, {
    enabled: !disableGestures
  }); // Perform some check on first mount

  useMount(function () {
    if (!Number.isInteger(itemsPerSlide)) {
      throw new Error("itemsPerSlide should be an integer.");
    }

    if (itemsPerSlide > items.length) {
      throw new Error("The itemsPerSlide prop can't be greater than the total length of the items you provide.");
    }

    if (itemsPerSlide < 1) {
      throw new Error("The itemsPerSlide prop can't be less than 1.");
    }

    if (!shouldResizeOnWindowResize) {
      console.warn('You set shouldResizeOnWindowResize={false}; be aware that the carousel could behave in a strange way if you also use the fullscreen functionality or if you change the mobile orientation.');
    }

    if (initialActiveItem < 0) {
      console.warn('The initialActiveItem cannot be less than 0.');
    }

    if (initialActiveItem > items.length) {
      console.warn('The initialActiveItem cannot be greater than the total length of the items you provide.');
    }
  });
  useMount(function () {
    function handleVisibilityChange() {
      if (document.hidden) {
        windowIsHidden.current = true;
      } else {
        windowIsHidden.current = false;
      }
    }

    if (getIsBrowser()) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return function () {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  });
  useEffect(function () {
    if (shouldResizeOnWindowResize) {
      window.addEventListener('resize', handleResize);
      return function () {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [handleResize, shouldResizeOnWindowResize]);
  useEffect(function () {
    if (carouselTrackWrapperRef.current) {
      if (carouselSlideAxis === 'x') {
        carouselTrackWrapperRef.current.style.top = '0px';
      }

      if (carouselSlideAxis === 'y') {
        carouselTrackWrapperRef.current.style.left = '0px';
      }
    }
  }, [carouselSlideAxis]);
  useMount(function () {
    if (initialActiveItem > 0 && initialActiveItem <= items.length) {
      slideToItem({
        to: initialActiveItem,
        immediate: true
      });
      setActiveItem(initialActiveItem);
    }
  });

  function setSlideActionType(type) {
    slideActionType.current = type;
  }

  function getSlideActionType() {
    return slideActionType.current;
  }

  function setActiveItem(newItem) {
    activeItem.current = newItem;
  }

  function _getCurrentActiveItem() {
    return activeItem.current;
  }

  function getIsAnimating() {
    return isAnimating.current;
  }

  function setIsAnimating(val) {
    isAnimating.current = val;
  }

  function setIsDragging(val) {
    isDragging.current = val;
  }

  function getIsDragging() {
    return isDragging.current;
  }

  function getPrevItem() {
    var currentActiveItem = _getCurrentActiveItem();

    if (currentActiveItem === 0) {
      return items.length - 1;
    }

    return currentActiveItem - 1;
  }

  function getNextItem() {
    var currentActiveItem = _getCurrentActiveItem();

    if (currentActiveItem === items.length - 1) {
      return 0;
    }

    return currentActiveItem + 1;
  }

  function getIsNextItem(id) {
    var itemIndex = findItemIndex(id);

    var activeItem = _getCurrentActiveItem();

    if (withLoop && activeItem === items.length - 1) {
      return itemIndex === 0;
    }

    return itemIndex === activeItem + 1;
  }

  function getIsPrevItem(id) {
    var itemIndex = findItemIndex(id);

    var activeItem = _getCurrentActiveItem();

    if (withLoop && activeItem === 0) {
      return itemIndex === items.length - 1;
    }

    return itemIndex === activeItem - 1;
  }

  function findItemIndex(id) {
    return items.findIndex(function (item) {
      return item.id === id;
    });
  }

  function slideToItem(_ref10) {
    var from = _ref10.from,
        to = _ref10.to,
        _ref10$immediate = _ref10.immediate,
        immediate = _ref10$immediate === void 0 ? false : _ref10$immediate,
        _ref10$onRest = _ref10.onRest,
        _onRest = _ref10$onRest === void 0 ? function () {} : _ref10$onRest;

    if (!immediate) {
      setActiveItem(to);
      setIsAnimating(true);
      emitObservable({
        eventName: 'onSlideStartChange',
        nextItem: to,
        slideActionType: getSlideActionType()
      });
    }

    function getFromValue() {
      if (from) {
        return {
          from: _defineProperty({}, carouselSlideAxis, from)
        };
      }

      return {};
    }

    setCarouselStyles.start(_objectSpread(_objectSpread({}, getFromValue()), {}, {
      to: _defineProperty({}, carouselSlideAxis, -(getSlideValue() * to)),
      immediate: immediate,
      onRest: function onRest(val) {
        if (val.finished) {
          setIsDragging(false);
          setIsAnimating(false);

          _onRest();

          if (!immediate) {
            emitObservable({
              eventName: 'onSlideChange',
              currentItem: _getCurrentActiveItem(),
              slideActionType: getSlideActionType()
            });
          }
        }
      }
    }));

    if (enableThumbsWrapperScroll && withThumbs && !immediate) {
      handleThumbsScroll(to);
    }
  }

  function getWrapperFromValue(element) {
    if (element.style.transform === 'none') {
      return 0;
    }

    var values = element.style.transform.split(/\w+\(|\);?/);
    return Number(values[1].split(/,\s?/g)[carouselSlideAxis === 'x' ? 0 : 1].replace('px', ''));
  }

  function getIsFirstItem() {
    return _getCurrentActiveItem() === 0;
  }

  function getIsLastItem() {
    return _getCurrentActiveItem() === items.length - 1;
  }

  function slideToPrevItem() {
    if (!withLoop && _getCurrentActiveItem() === 0 || windowIsHidden.current) {
      return;
    }

    setSlideActionType('prev');

    if (getIsFirstItem()) {
      slideToItem({
        from: -(Math.abs(getWrapperFromValue(carouselTrackWrapperRef.current)) + getSlideValue() * items.length),
        to: items.length - 1
      });
    } else {
      slideToItem({
        to: getPrevItem()
      });
    }
  }

  function slideToNextItem() {
    if (!withLoop && _getCurrentActiveItem() === internalItems.length - 1 || windowIsHidden.current) {
      return;
    }

    setSlideActionType('next');

    if (getIsLastItem()) {
      slideToItem({
        from: getWrapperFromValue(carouselTrackWrapperRef.current) + getSlideValue() * items.length,
        to: 0
      });
    } else {
      slideToItem({
        to: getNextItem()
      });
    }
  }

  function _slideToItem(item) {
    var itemIndex = 0;

    if (typeof item === 'string') {
      itemIndex = items.findIndex(function (_item) {
        return _item.id === item;
      });
    } else {
      itemIndex = item;
    }

    if (itemIndex >= items.length) {
      throw Error("The item you want to slide to doesn't exist. This could be due to the fact that \n        you provide a wrong id or a higher numeric index.");
    }

    if (itemIndex === _getCurrentActiveItem()) {
      return;
    }

    var currentItem = findItemIndex(items[_getCurrentActiveItem()].id);
    var newActiveItem = findItemIndex(items[itemIndex].id);

    if (newActiveItem > currentItem) {
      setSlideActionType('next');
    } else {
      setSlideActionType('prev');
    }

    slideToItem({
      to: itemIndex
    });
  }

  var contextProps = {
    useListenToCustomEvent: useListenToCustomEvent,
    getIsFullscreen: getIsFullscreen,
    enterFullscreen: enterFullscreen,
    exitFullscreen: exitFullscreen,
    getIsAnimating: getIsAnimating,
    getIsDragging: getIsDragging,
    getIsNextItem: getIsNextItem,
    getIsPrevItem: getIsPrevItem,
    slideToPrevItem: slideToPrevItem,
    slideToNextItem: slideToNextItem,
    slideToItem: _slideToItem,
    getIsActiveItem: function getIsActiveItem(id) {
      return findItemIndex(id) === _getCurrentActiveItem();
    },
    getCurrentActiveItem: function getCurrentActiveItem() {
      return {
        id: items[_getCurrentActiveItem()].id,
        index: _getCurrentActiveItem()
      };
    }
  };

  function getItemWidthValue() {
    return "repeat(".concat(internalItems.length, ", calc(calc(100% - ").concat(gutter * (itemsPerSlide - 1), "px) / ").concat(itemsPerSlide, "))");
  }

  function getPercentageValue() {
    return "calc(100% - ".concat(adjacentItemsPx * 2, "px)");
  }

  var carouselFragment = jsx(UseSpringCarouselContext.Provider, Object.assign({
    value: contextProps
  }, {
    children: jsx("div", Object.assign({
      ref: mainCarouselWrapperRef,
      "data-testid": "use-spring-carousel-wrapper",
      style: {
        display: 'flex',
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }
    }, {
      children: jsx(animated.div, Object.assign({}, bindDrag(), {
        "data-testid": "use-spring-carousel-animated-wrapper",
        style: _objectSpread((_objectSpread2 = {
          display: 'grid',
          gridGap: "".concat(gutter, "px")
        }, _defineProperty(_objectSpread2, carouselSlideAxis === 'x' ? 'gridTemplateColumns' : 'gridTemplateRows', getItemWidthValue()), _defineProperty(_objectSpread2, "top", 0), _defineProperty(_objectSpread2, "left", 0), _defineProperty(_objectSpread2, "width", carouselSlideAxis === 'x' ? getPercentageValue() : '100%'), _defineProperty(_objectSpread2, "height", carouselSlideAxis === 'y' ? getPercentageValue() : '100%'), _defineProperty(_objectSpread2, "position", 'relative'), _objectSpread2), carouselStyles),
        ref: function ref(_ref11) {
          if (_ref11) {
            carouselTrackWrapperRef.current = _ref11;

            if (withLoop) {
              adjustCarouselWrapperPosition(_ref11);
            }
          }
        }
      }, {
        children: internalItems.map(function (_ref12, index) {
          var id = _ref12.id,
              renderItem = _ref12.renderItem;
          return jsx("div", Object.assign({
            className: "use-spring-carousel-item",
            "data-testid": "use-spring-carousel-item-wrapper",
            style: {
              display: 'flex',
              position: 'relative'
            }
          }, {
            children: renderItem
          }), "".concat(id, "-").concat(index));
        })
      }), void 0)
    }), void 0)
  }), void 0);
  var thumbsFragment = jsx(UseSpringCarouselContext.Provider, Object.assign({
    value: contextProps
  }, {
    children: _thumbsFragment
  }), void 0);
  return _objectSpread({
    carouselFragment: carouselFragment,
    thumbsFragment: thumbsFragment
  }, contextProps);
}

var UseTransitionCarouselContext = createContext(undefined);

function useTransitionCarouselContext() {
  var context = useContext(UseTransitionCarouselContext);

  if (!context) {
    throw new Error("useTransitionCarouselContext isn't being used within the useTransitionCarousel context; \n    use the context only inside a component that is rendered within the Carousel.");
  }

  return context;
}

function useTransitionCarousel(_ref13) {
  var items = _ref13.items,
      _ref13$withLoop = _ref13.withLoop,
      withLoop = _ref13$withLoop === void 0 ? false : _ref13$withLoop,
      _ref13$withThumbs = _ref13.withThumbs,
      withThumbs = _ref13$withThumbs === void 0 ? false : _ref13$withThumbs,
      _ref13$springConfig = _ref13.springConfig,
      springConfig = _ref13$springConfig === void 0 ? config.default : _ref13$springConfig,
      _ref13$thumbsSlideAxi = _ref13.thumbsSlideAxis,
      thumbsSlideAxis = _ref13$thumbsSlideAxi === void 0 ? 'x' : _ref13$thumbsSlideAxi,
      _ref13$enableThumbsWr = _ref13.enableThumbsWrapperScroll,
      enableThumbsWrapperScroll = _ref13$enableThumbsWr === void 0 ? true : _ref13$enableThumbsWr,
      _ref13$draggingSlideT = _ref13.draggingSlideTreshold,
      draggingSlideTreshold = _ref13$draggingSlideT === void 0 ? 50 : _ref13$draggingSlideT,
      prepareThumbsData = _ref13.prepareThumbsData,
      toPrevItemSpringProps = _ref13.toPrevItemSpringProps,
      toNextItemSpringProps = _ref13.toNextItemSpringProps,
      _ref13$disableGesture = _ref13.disableGestures,
      disableGestures = _ref13$disableGesture === void 0 ? false : _ref13$disableGesture,
      _ref13$springAnimatio = _ref13.springAnimationProps,
      springAnimationProps = _ref13$springAnimatio === void 0 ? {
    initial: {
      opacity: 1,
      position: 'relative'
    },
    from: {
      opacity: 0,
      position: 'absolute'
    },
    enter: {
      opacity: 1,
      position: 'relative'
    },
    leave: {
      opacity: 0,
      position: 'absolute'
    }
  } : _ref13$springAnimatio;
  var slideActionType = useRef('next');
  var mainCarouselWrapperRef = useRef(null);
  var isAnimating = useRef(false);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      activeItem = _useState2[0],
      setActiveItem = _useState2[1];

  var _useCustomEventsModul2 = useCustomEventsModule(),
      emitObservable = _useCustomEventsModul2.emitObservable,
      useListenToCustomEvent = _useCustomEventsModul2.useListenToCustomEvent;

  var _useFullscreenModule2 = useFullscreenModule({
    emitObservable: emitObservable,
    mainCarouselWrapperRef: mainCarouselWrapperRef
  }),
      enterFullscreen = _useFullscreenModule2.enterFullscreen,
      exitFullscreen = _useFullscreenModule2.exitFullscreen,
      getIsFullscreen = _useFullscreenModule2.getIsFullscreen;

  var _useThumbsModule2 = useThumbsModule({
    items: items,
    withThumbs: withThumbs,
    thumbsSlideAxis: thumbsSlideAxis,
    springConfig: springConfig,
    prepareThumbsData: prepareThumbsData
  }),
      _thumbsFragment = _useThumbsModule2.thumbsFragment,
      handleThumbsScroll = _useThumbsModule2.handleThumbsScroll;

  var bindSwipe = useDrag(function (_ref14) {
    var last = _ref14.last,
        _ref14$movement = _slicedToArray(_ref14.movement, 1),
        mx = _ref14$movement[0];

    if (getIsAnimating()) {
      return;
    }

    if (last) {
      var prevItemTreshold = mx > draggingSlideTreshold;
      var nextItemTreshold = mx < -draggingSlideTreshold;
      var isFirstItem = activeItem === 0;
      var isLastItem = activeItem === items.length - 1;

      if (nextItemTreshold) {
        if (!withLoop && isLastItem) {
          return;
        }

        slideToNextItem();
        emitObservable({
          eventName: 'onLeftSwipe'
        });
      } else if (prevItemTreshold) {
        if (!withLoop && isFirstItem) {
          return;
        }

        slideToPrevItem();
        emitObservable({
          eventName: 'onRightSwipe'
        });
      }
    }
  }, {
    enabled: !disableGestures
  });

  function getTransitionConfig() {
    var slideActionType = getSlideActionType();

    if (slideActionType === 'prev' && toPrevItemSpringProps) {
      return {
        initial: _objectSpread({}, springAnimationProps.initial),
        from: _objectSpread({}, toPrevItemSpringProps.from),
        enter: _objectSpread({}, toPrevItemSpringProps.enter),
        leave: _objectSpread({}, toPrevItemSpringProps.leave)
      };
    }

    if (slideActionType === 'next' && toNextItemSpringProps) {
      return {
        initial: _objectSpread({}, springAnimationProps.initial),
        from: _objectSpread({}, toNextItemSpringProps.from),
        enter: _objectSpread({}, toNextItemSpringProps.enter),
        leave: _objectSpread({}, toNextItemSpringProps.leave)
      };
    }

    return {
      initial: _objectSpread({}, springAnimationProps.initial),
      from: _objectSpread({}, springAnimationProps.from),
      enter: _objectSpread({}, springAnimationProps.enter),
      leave: _objectSpread({}, springAnimationProps.leave)
    };
  }

  var transitions = useTransition(activeItem, _objectSpread(_objectSpread({
    config: springConfig
  }, getTransitionConfig()), {}, {
    onStart: function onStart() {
      return setIsAnimating(true);
    },
    keys: null,
    onRest: function onRest(val) {
      if (val.finished) {
        setIsAnimating(false);
        emitObservable({
          eventName: 'onSlideChange',
          currentItem: activeItem,
          slideActionType: getSlideActionType()
        });
      }
    }
  }));
  var itemsFragment = transitions(function (styles, item) {
    return jsx(animated.div, Object.assign({
      style: _objectSpread(_objectSpread({}, styles), {}, {
        flex: '1 0 100%',
        width: '100%',
        height: '100%'
      })
    }, {
      children: items[item].renderItem
    }), void 0);
  });

  function getIsAnimating() {
    return isAnimating.current;
  }

  function setIsAnimating(val) {
    isAnimating.current = val;
  }

  function setSlideActionType(type) {
    slideActionType.current = type;
  }

  function getSlideActionType() {
    return slideActionType.current;
  }

  function slideToItem(item) {
    var itemIndex = 0;

    if (typeof item === 'string') {
      itemIndex = items.findIndex(function (_item) {
        return _item.id === item;
      });
    } else {
      itemIndex = item;
    }

    if (itemIndex >= items.length) {
      throw Error("The item you want to slide to doesn't exist. This could be due to the fact that \n        you provide a wrong id or a higher numeric index.");
    }

    if (itemIndex === activeItem) {
      return;
    }

    var currentItem = findItemIndex(items[activeItem].id);
    var newActiveItem = findItemIndex(items[itemIndex].id);
    emitObservable({
      eventName: 'onSlideStartChange',
      nextItem: newActiveItem,
      slideActionType: getSlideActionType()
    });

    if (newActiveItem > currentItem) {
      setSlideActionType('next');
    } else {
      setSlideActionType('prev');
    }

    setActiveItem(itemIndex);

    if (enableThumbsWrapperScroll && withThumbs) {
      handleThumbsScroll(itemIndex);
    }
  }

  function slideToNextItem() {
    var isLastItem = activeItem === items.length - 1;

    if (withLoop) {
      setSlideActionType('next');

      if (isLastItem) {
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: 0,
          slideActionType: getSlideActionType()
        });
        setActiveItem(0);
      } else {
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: activeItem + 1,
          slideActionType: getSlideActionType()
        });
        setActiveItem(activeItem + 1);
      }
    } else {
      if (!isLastItem) {
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: activeItem + 1,
          slideActionType: getSlideActionType()
        });
        setSlideActionType('next');
        setActiveItem(activeItem + 1);
      }
    }
  }

  function slideToPrevItem() {
    var isFirstItem = activeItem === 0;

    if (withLoop) {
      setSlideActionType('prev');

      if (isFirstItem) {
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: items.length - 1,
          slideActionType: getSlideActionType()
        });
        setActiveItem(items.length - 1);
      } else {
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: activeItem - 1,
          slideActionType: getSlideActionType()
        });
        setActiveItem(activeItem - 1);
      }
    } else {
      if (!isFirstItem) {
        setSlideActionType('prev');
        emitObservable({
          eventName: 'onSlideStartChange',
          nextItem: activeItem - 1,
          slideActionType: getSlideActionType()
        });
        setActiveItem(activeItem - 1);
      }
    }
  }

  function findItemIndex(id) {
    return items.findIndex(function (item) {
      return item.id === id;
    });
  }

  function getIsNextItem(id) {
    var itemIndex = findItemIndex(id);

    if (withLoop && activeItem === items.length - 1) {
      return itemIndex === 0;
    }

    return itemIndex === activeItem + 1;
  }

  function getIsPrevItem(id) {
    var itemIndex = findItemIndex(id);

    if (withLoop && activeItem === 0) {
      return itemIndex === items.length - 1;
    }

    return itemIndex === activeItem - 1;
  }

  var contextProps = {
    activeItem: activeItem,
    slideToItem: slideToItem,
    slideToNextItem: slideToNextItem,
    slideToPrevItem: slideToPrevItem,
    enterFullscreen: enterFullscreen,
    exitFullscreen: exitFullscreen,
    useListenToCustomEvent: useListenToCustomEvent,
    getIsNextItem: getIsNextItem,
    getIsPrevItem: getIsPrevItem,
    getIsAnimating: getIsAnimating,
    getIsFullscreen: getIsFullscreen,
    getIsActiveItem: function getIsActiveItem(id) {
      return findItemIndex(id) === activeItem;
    },
    getCurrentActiveItem: function getCurrentActiveItem() {
      return {
        id: items[activeItem].id,
        index: activeItem
      };
    }
  };
  var carouselFragment = jsx(UseTransitionCarouselContext.Provider, Object.assign({
    value: contextProps
  }, {
    children: jsx("div", Object.assign({
      ref: mainCarouselWrapperRef
    }, bindSwipe(), {
      style: {
        display: 'flex',
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }
    }, {
      children: itemsFragment
    }), void 0)
  }), void 0);
  var thumbsFragment = jsx(UseTransitionCarouselContext.Provider, Object.assign({
    value: contextProps
  }, {
    children: _thumbsFragment
  }), void 0);
  return _objectSpread({
    carouselFragment: carouselFragment,
    thumbsFragment: thumbsFragment
  }, contextProps);
}

export { useSpringCarousel, useSpringCarouselContext, useTransitionCarousel, useTransitionCarouselContext };